/**
 * PHARMAKON — thermal lot labels: the pure part of LabelSheet (refonte WP-19 · F4; D-15, D-16;
 * design/ux/01_foundations.md §3.16, 02_achats.md §6.7).
 *
 * `prepareLabels` turns the lots chosen in a label dialog into what the sheet renders: one entry
 * per lot (text, price, bars), the ordered list of printed pages (one label per page, N copies per
 * lot) and the reasons a lot cannot print (M-S3-21 / M-S3-22), so the dialog disables it with the
 * right sentence instead of printing a wrong label. Bars come from the shared Code 128 encoder
 * (`@pharmakon/shared/barcode`, the server copy is byte-identical), never from a second encoder.
 */
import { largeursCode128C } from "@pharmakon/shared/barcode/code128.js";
import { analyserCodeLot } from "@pharmakon/shared/barcode/code-lot.js";
import {
  ETIQUETTE_COPIES,
  ETIQUETTE_MODULE_MIN_MM,
  ETIQUETTE_ZONE_CALME_MODULES,
  PRINT_MESSAGES,
  etiquetteFormat,
} from "@/config/print.config.js";
import { fmtMoney } from "@/config/locale.config.js";
import { UNKNOWN_VALUE } from "@/lib/number-format.js";

/** Why a lot is not printable. */
export const LABEL_ERRORS = Object.freeze({
  CODE_ABSENT: "CODE_ABSENT",
  COPIES_INVALIDES: "COPIES_INVALIDES",
  CODE_TROP_LONG: "CODE_TROP_LONG",
});

const MESSAGE = Object.freeze({
  [LABEL_ERRORS.CODE_ABSENT]: PRINT_MESSAGES.codeAbsent,
  [LABEL_ERRORS.COPIES_INVALIDES]: PRINT_MESSAGES.copiesInvalides,
  [LABEL_ERRORS.CODE_TROP_LONG]: PRINT_MESSAGES.codeTropLong,
});

/**
 * Bars of a lot code at a label format: the bar / space runs in modules and the physical size.
 * Quiet zones of ≥ 10 modules each side are part of the width; `fits` = the symbol and its quiet
 * zones fit inside the label's safe area (ux/01 §3.16 fit check).
 *
 * @param {string} code  a Pharmakon lot code (12 digits, Code 128 subset C)
 * @param {string} formatKey  "40x25" | "50x30"
 */
export function barcodeGeometry(code, formatKey) {
  const f = etiquetteFormat(formatKey);
  const largeurs = largeursCode128C(code);
  const modulesSymbole = largeurs.reduce((a, b) => a + b, 0);
  const zone = ETIQUETTE_ZONE_CALME_MODULES;
  const modulesTotal = modulesSymbole + 2 * zone;
  const moduleMm = Math.max(ETIQUETTE_MODULE_MIN_MM, f.module_mm);
  const largeurMm = modulesTotal * moduleMm;
  const utileMm = f.largeur_mm - 2 * f.padding_mm;
  /** Bars as [x, width] in modules (quiet zone offset included). */
  const barres = [];
  let x = zone;
  largeurs.forEach((w, i) => {
    if (i % 2 === 0) barres.push([x, w]);
    x += w;
  });
  return {
    barres,
    modulesSymbole,
    modulesTotal,
    zoneCalme: zone,
    moduleMm,
    largeurMm: Math.round(largeurMm * 100) / 100,
    hauteurMm: f.barre_hauteur_mm,
    utileMm,
    fits: largeurMm <= utileMm,
  };
}

const clean = (s) => String(s ?? "").replace(/\s+/g, " ").trim();

/**
 * The product text of a label. 40 × 25: one block (name, dosage, forme, conditionnement) clamped to
 * two lines. 50 × 30: « name dosage » then « forme · conditionnement » (02 §6.7 layouts).
 */
export function labelProductText(produit, formatKey) {
  const f = etiquetteFormat(formatKey);
  const nom = clean(produit?.nom);
  const dosage = clean(produit?.dosage);
  const forme = clean(produit?.forme);
  const cond = clean(produit?.conditionnement);
  if (!f.detail_separe) {
    return { titre: [nom, dosage, forme, cond].filter(Boolean).join(" "), detail: "" };
  }
  return { titre: [nom, dosage].filter(Boolean).join(" "), detail: [forme, cond].filter(Boolean).join(" · ") };
}

/** Number of copies: integer within ETIQUETTE_COPIES, else null. */
export function normalizeCopies(copies) {
  const n = typeof copies === "string" && copies.trim() !== "" ? Number(copies) : copies;
  if (!Number.isInteger(n) || n < ETIQUETTE_COPIES.MIN || n > ETIQUETTE_COPIES.MAX) return null;
  return n;
}

/**
 * @typedef {Object} LabelInput
 * @property {string} key       stable id (the lot id)
 * @property {{ nom: string, dosage?: string, forme?: string, conditionnement?: string }} produit
 * @property {string|number|null} [prix]  the lot's PPA (decimal string from the server)
 * @property {string|null} code  the lot code (CodeBarreLot.code); null = not yet attributed
 * @property {number} [copies]   default 1
 */

/**
 * @param {LabelInput[]} labels
 * @param {{ format?: string, pharmacieNom?: string, showPharmacie?: boolean, showPrix?: boolean }} [opts]
 * @returns {{ format: object, lots: any[], pages: Array<{ id: string, lotKey: string, copie: number }>, errors: Array<{ key: string, code: string, message: string }>, total: number }}
 */
export function prepareLabels(labels, opts = {}) {
  const format = etiquetteFormat(opts.format);
  const pharmacie = opts.showPharmacie === false ? "" : clean(opts.pharmacieNom);
  const showPrix = opts.showPrix !== false;
  const lots = [];
  const errors = [];
  const pages = [];
  for (const l of labels ?? []) {
    const key = String(l?.key ?? "");
    const analyse = analyserCodeLot(l?.code ?? "");
    if (!analyse.valide) {
      errors.push({ key, code: LABEL_ERRORS.CODE_ABSENT, message: MESSAGE.CODE_ABSENT });
      continue;
    }
    const copies = normalizeCopies(l?.copies ?? 1);
    if (copies === null) {
      errors.push({ key, code: LABEL_ERRORS.COPIES_INVALIDES, message: MESSAGE.COPIES_INVALIDES });
      continue;
    }
    const geometrie = barcodeGeometry(analyse.code, format.cle);
    if (!geometrie.fits) {
      errors.push({ key, code: LABEL_ERRORS.CODE_TROP_LONG, message: MESSAGE.CODE_TROP_LONG });
      continue;
    }
    const prix = showPrix ? fmtMoney(l?.prix) : "";
    lots.push({
      key,
      symbolId: `pk-lbl-${lots.length}`,
      code: analyse.code,
      ...labelProductText(l?.produit, format.cle),
      prix: prix === UNKNOWN_VALUE ? "" : prix,
      // A long price prints one size smaller so it never meets the code (WP-26 fit, A-53).
      prixLong: prix !== UNKNOWN_VALUE && prix.length > format.prix_car_max,
      pharmacie,
      geometrie,
      copies,
    });
    for (let c = 1; c <= copies; c += 1) pages.push({ id: `${key}:${c}`, lotKey: key, copie: c });
  }
  return { format, lots, pages, errors, total: pages.length };
}
